import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { 
  RefreshCw, Loader2, CheckCircle2, XCircle, AlertTriangle, 
  Link as LinkIcon, Unlink, Zap, Bike, Navigation, Search
} from 'lucide-react';
import { Input } from './ui/input';
import useDeviceDetection from '../hooks/useDeviceDetection';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const GoFleetDeviceManager = ({ open, onOpenChange, getAuthHeader, onRefresh }) => {
  const [loading, setLoading] = useState(false);
  const [autoMatching, setAutoMatching] = useState(false);
  const [matchingStatus, setMatchingStatus] = useState(null);
  const [matchResults, setMatchResults] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [searchTerm, setSearchTerm] = useState('');
  const { isMobile } = useDeviceDetection();

  useEffect(() => {
    if (open) {
      fetchMatchingStatus();
    }
  }, [open]);

  const fetchMatchingStatus = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/gofleet/matching-status`, {
        headers: getAuthHeader()
      });
      setMatchingStatus(response.data);
    } catch (error) {
      toast.error('Failed to fetch matching status');
    } finally {
      setLoading(false);
    }
  };

  const handleAutoMatch = async () => {
    setAutoMatching(true);
    try {
      const response = await axios.post(`${API}/gofleet/auto-match-devices`, {}, {
        headers: getAuthHeader()
      });
      setMatchResults(response.data);
      toast.success(`Auto-match complete: ${response.data.summary.newly_matched} new matches`);
      fetchMatchingStatus();
      if (onRefresh) onRefresh();
    } catch (error) {
      toast.error('Auto-match failed: ' + (error.response?.data?.detail || error.message));
    } finally {
      setAutoMatching(false);
    }
  };

  const handleUnlink = async (motorcycleId, plateName) => {
    try {
      await axios.delete(`${API}/gofleet/unlink-device/${motorcycleId}`, {
        headers: getAuthHeader()
      });
      toast.success(`Device unlinked from ${plateName}`);
      fetchMatchingStatus();
      if (onRefresh) onRefresh();
    } catch (error) {
      toast.error('Failed to unlink device');
    }
  };

  const filterItems = (items, searchField = 'plate_number') => {
    if (!searchTerm) return items;
    return items.filter(item => 
      (item[searchField] || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (item.device_name || '').toLowerCase().includes(searchTerm.toLowerCase())
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto" data-testid="gofleet-device-manager-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-xl">
            <Navigation className="w-6 h-6 text-blue-600" />
            GoFleet Device Manager
          </DialogTitle>
          <DialogDescription>
            Auto-match motorcycles to GoFleet GPS devices based on plate numbers (last 5 characters)
          </DialogDescription>
        </DialogHeader>

        {loading && !matchingStatus ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
            <span className="ml-3">Loading...</span>
          </div>
        ) : (
          <div className="space-y-4">
            {/* Summary Cards */}
            <div className={`grid ${isMobile ? 'grid-cols-2' : 'grid-cols-4'} gap-3`}>
              <Card className="bg-slate-50">
                <CardContent className={`${isMobile ? 'p-3' : 'pt-4'}`}>
                  <div className="flex items-center gap-2">
                    <div className={`${isMobile ? 'p-1.5' : 'p-2'} bg-blue-100 rounded-lg`}>
                      <Bike className={`${isMobile ? 'w-4 h-4' : 'w-5 h-5'} text-blue-600`} />
                    </div>
                    <div>
                      <p className={`${isMobile ? 'text-xl' : 'text-2xl'} font-bold`}>{matchingStatus?.total_motorcycles || 0}</p>
                      <p className={`${isMobile ? 'text-[10px]' : 'text-xs'} text-slate-500`}>{isMobile ? 'Total' : 'Total Motorcycles'}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-green-50">
                <CardContent className={`${isMobile ? 'p-3' : 'pt-4'}`}>
                  <div className="flex items-center gap-2">
                    <div className={`${isMobile ? 'p-1.5' : 'p-2'} bg-green-100 rounded-lg`}>
                      <LinkIcon className={`${isMobile ? 'w-4 h-4' : 'w-5 h-5'} text-green-600`} />
                    </div>
                    <div>
                      <p className={`${isMobile ? 'text-xl' : 'text-2xl'} font-bold text-green-700`}>{matchingStatus?.linked_count || 0}</p>
                      <p className={`${isMobile ? 'text-[10px]' : 'text-xs'} text-slate-500`}>{isMobile ? 'Linked' : 'Linked to GoFleet'}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-amber-50">
                <CardContent className={`${isMobile ? 'p-3' : 'pt-4'}`}>
                  <div className="flex items-center gap-2">
                    <div className={`${isMobile ? 'p-1.5' : 'p-2'} bg-amber-100 rounded-lg`}>
                      <Unlink className={`${isMobile ? 'w-4 h-4' : 'w-5 h-5'} text-amber-600`} />
                    </div>
                    <div>
                      <p className={`${isMobile ? 'text-xl' : 'text-2xl'} font-bold text-amber-700`}>{matchingStatus?.unlinked_count || 0}</p>
                      <p className={`${isMobile ? 'text-[10px]' : 'text-xs'} text-slate-500`}>{isMobile ? 'Not Linked' : 'Not Linked'}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-purple-50">
                <CardContent className={`${isMobile ? 'p-3' : 'pt-4'}`}>
                  <div className="flex items-center gap-2">
                    <div className={`${isMobile ? 'p-1.5' : 'p-2'} bg-purple-100 rounded-lg`}>
                      <Zap className={`${isMobile ? 'w-4 h-4' : 'w-5 h-5'} text-purple-600`} />
                    </div>
                    <div>
                      <Button 
                        onClick={handleAutoMatch} 
                        disabled={autoMatching}
                        variant="outline"
                        className="gap-2 bg-purple-100 text-purple-700 border-purple-300 hover:bg-purple-200 hover:text-purple-800"
                        size="sm"
                        data-testid="gofleet-auto-match-btn"
                      >
                        {autoMatching ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <RefreshCw className="w-4 h-4" />
                        )}
                        {isMobile ? 'Match' : 'Auto-Match'}
                      </Button>
                      <p className={`${isMobile ? 'text-[10px]' : 'text-xs'} text-slate-500 mt-1`}>{isMobile ? 'By plate #' : 'Match by plate #'}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Auto-Match Results */}
            {matchResults && (
              <Card className="border-2 border-blue-200 bg-blue-50">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4 text-blue-600" />
                    Auto-Match Results
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-5 gap-4 text-center">
                    <div>
                      <p className="text-2xl font-bold text-green-600">{matchResults.summary.newly_matched}</p>
                      <p className="text-xs text-slate-600">Newly Matched</p>
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-blue-600">{matchResults.summary.already_linked}</p>
                      <p className="text-xs text-slate-600">Already Linked</p>
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-amber-600">{matchResults.summary.unmatched}</p>
                      <p className="text-xs text-slate-600">Unmatched</p>
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-red-600">{matchResults.summary.duplicates_requiring_manual}</p>
                      <p className="text-xs text-slate-600">Duplicates</p>
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-slate-600">{matchResults.summary.total_gofleet_devices}</p>
                      <p className="text-xs text-slate-600">GoFleet Devices</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Tabs */}
            <Tabs value={activeTab} onValueChange={setActiveTab}>
              <TabsList className={`grid w-full ${isMobile ? 'grid-cols-4 gap-0.5' : 'grid-cols-4'}`}>
                <TabsTrigger value="overview" className={`gap-1 ${isMobile ? 'text-xs px-1' : ''}`}>
                  <CheckCircle2 className={`${isMobile ? 'w-3 h-3' : 'w-4 h-4'}`} />
                  {isMobile ? `(${matchingStatus?.linked_count || 0})` : `Linked (${matchingStatus?.linked_count || 0})`}
                </TabsTrigger>
                <TabsTrigger value="unlinked" className={`gap-1 ${isMobile ? 'text-xs px-1' : ''}`}>
                  <Unlink className={`${isMobile ? 'w-3 h-3' : 'w-4 h-4'}`} />
                  {isMobile ? `(${matchingStatus?.unlinked_count || 0})` : `Unlinked (${matchingStatus?.unlinked_count || 0})`}
                </TabsTrigger>
                <TabsTrigger value="matched" className={`gap-1 ${isMobile ? 'text-xs px-1' : ''}`} disabled={!matchResults}>
                  <Zap className={`${isMobile ? 'w-3 h-3' : 'w-4 h-4'}`} />
                  {isMobile ? `(${matchResults?.matched?.length || 0})` : `New Matches (${matchResults?.matched?.length || 0})`}
                </TabsTrigger>
                <TabsTrigger value="duplicates" className={`gap-1 ${isMobile ? 'text-xs px-1' : ''}`} disabled={!matchResults?.duplicates?.length}>
                  <AlertTriangle className={`${isMobile ? 'w-3 h-3' : 'w-4 h-4'}`} />
                  {isMobile ? `(${matchResults?.duplicates?.length || 0})` : `Duplicates (${matchResults?.duplicates?.length || 0})`}
                </TabsTrigger>
              </TabsList>

              {/* Search */}
              <div className="relative mt-4">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  placeholder="Search by plate number or device name..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>

              {/* Linked Motorcycles */}
              <TabsContent value="overview" className="mt-4">
                {isMobile ? (
                  /* Mobile Card View */
                  <div className="border rounded-lg divide-y divide-slate-100 max-h-[300px] overflow-y-auto">
                    {filterItems(matchingStatus?.linked || []).length === 0 ? (
                      <div className="text-center text-slate-500 py-8">No linked motorcycles found</div>
                    ) : (
                      filterItems(matchingStatus?.linked || []).map((item) => (
                        <div key={item.id} className="p-3 hover:bg-slate-50">
                          <div className="flex items-center justify-between mb-2">
                            <span className="font-medium">{item.plate_number}</span>
                            <Badge className="bg-green-100 text-green-700 text-xs">
                              <CheckCircle2 className="w-3 h-3 mr-1" />
                              Linked
                            </Badge>
                          </div>
                          <div className="flex items-center justify-between">
                            <span className="font-mono text-xs text-slate-500 truncate max-w-[180px]">
                              {item.gofleet_device_id?.substring(0, 15)}...
                            </span>
                            <Button 
                              size="sm" 
                              variant="ghost" 
                              onClick={() => handleUnlink(item.id, item.plate_number)}
                              className="text-red-600 hover:text-red-700 h-7 px-2"
                            >
                              <Unlink className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                ) : (
                  /* Desktop Table View */
                  <div className="border rounded-lg overflow-hidden max-h-[300px] overflow-y-auto">
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-slate-50">
                          <TableHead>Motorcycle Plate</TableHead>
                          <TableHead>GoFleet Device ID</TableHead>
                          <TableHead>Status</TableHead>
                          <TableHead>Actions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {filterItems(matchingStatus?.linked || []).map((item) => (
                          <TableRow key={item.id}>
                            <TableCell className="font-medium">{item.plate_number}</TableCell>
                            <TableCell className="font-mono text-xs">{item.gofleet_device_id?.substring(0, 20)}...</TableCell>
                            <TableCell>
                              <Badge className="bg-green-100 text-green-700">
                                <CheckCircle2 className="w-3 h-3 mr-1" />
                                Linked
                              </Badge>
                            </TableCell>
                            <TableCell>
                              <Button 
                                size="sm" 
                                variant="ghost" 
                                onClick={() => handleUnlink(item.id, item.plate_number)}
                                className="text-red-600 hover:text-red-700"
                              >
                                <Unlink className="w-4 h-4" />
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))}
                        {filterItems(matchingStatus?.linked || []).length === 0 && (
                          <TableRow>
                            <TableCell colSpan={4} className="text-center text-slate-500 py-8">
                              No linked motorcycles found
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </TabsContent>

              {/* Unlinked Motorcycles */}
              <TabsContent value="unlinked" className="mt-4">
                {isMobile ? (
                  /* Mobile Card View */
                  <div className="border rounded-lg divide-y divide-slate-100 max-h-[300px] overflow-y-auto">
                    {filterItems(matchingStatus?.unlinked || []).length === 0 ? (
                      <div className="text-center text-slate-500 py-8">All motorcycles are linked!</div>
                    ) : (
                      filterItems(matchingStatus?.unlinked || []).map((item) => {
                        const last5 = (item.plate_number || '').replace(/[\s\-\.]/g, '').slice(-5).toUpperCase();
                        return (
                          <div key={item.id} className="p-3 hover:bg-slate-50">
                            <div className="flex items-center justify-between">
                              <div>
                                <span className="font-medium">{item.plate_number}</span>
                                <Badge variant="outline" className="font-mono ml-2 text-xs">{last5 || '-'}</Badge>
                              </div>
                              <Badge className="bg-amber-100 text-amber-700 text-xs">
                                <XCircle className="w-3 h-3 mr-1" />
                                Not Linked
                              </Badge>
                            </div>
                          </div>
                        );
                      })
                    )}
                  </div>
                ) : (
                  /* Desktop Table View */
                  <div className="border rounded-lg overflow-hidden max-h-[300px] overflow-y-auto">
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-slate-50">
                          <TableHead>Motorcycle Plate</TableHead>
                          <TableHead>Last 5 Characters</TableHead>
                          <TableHead>Status</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {filterItems(matchingStatus?.unlinked || []).map((item) => {
                          const last5 = (item.plate_number || '').replace(/[\s\-\.]/g, '').slice(-5).toUpperCase();
                          return (
                            <TableRow key={item.id}>
                              <TableCell className="font-medium">{item.plate_number}</TableCell>
                              <TableCell>
                                <Badge variant="outline" className="font-mono">{last5 || '-'}</Badge>
                              </TableCell>
                              <TableCell>
                                <Badge className="bg-amber-100 text-amber-700">
                                  <XCircle className="w-3 h-3 mr-1" />
                                  Not Linked
                                </Badge>
                              </TableCell>
                            </TableRow>
                          );
                        })}
                        {filterItems(matchingStatus?.unlinked || []).length === 0 && (
                          <TableRow>
                            <TableCell colSpan={3} className="text-center text-slate-500 py-8">
                              All motorcycles are linked!
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </TabsContent>

              {/* Newly Matched */}
              <TabsContent value="matched" className="mt-4">
                {matchResults?.matched?.length > 0 ? (
                  <div className="border rounded-lg overflow-hidden max-h-[300px] overflow-y-auto">
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-green-50">
                          <TableHead>Motorcycle Plate</TableHead>
                          <TableHead>Matched Device</TableHead>
                          <TableHead>Matched On</TableHead>
                          <TableHead>Status</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {matchResults.matched.map((item, index) => (
                          <TableRow key={index} className="bg-green-50/50">
                            <TableCell className="font-medium">{item.plate_number}</TableCell>
                            <TableCell>{item.device_name}</TableCell>
                            <TableCell>
                              <Badge variant="outline" className="font-mono bg-green-100">{item.matched_on}</Badge>
                            </TableCell>
                            <TableCell>
                              <Badge className="bg-green-100 text-green-700">
                                <CheckCircle2 className="w-3 h-3 mr-1" />
                                Newly Matched
                              </Badge>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                ) : (
                  <div className="text-center py-10 text-slate-500">
                    <Zap className="w-12 h-12 mx-auto mb-2 opacity-50" />
                    <p>Run Auto-Match to see new matches</p>
                  </div>
                )}
              </TabsContent>

              {/* Duplicates - Highlighted */}
              <TabsContent value="duplicates" className="mt-4">
                {matchResults?.duplicates?.length > 0 ? (
                  <div className="space-y-4">
                    <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                      <div className="flex items-center gap-2 text-red-800">
                        <AlertTriangle className="w-5 h-5" />
                        <span className="font-medium">Duplicates Found - Manual Assignment Required</span>
                      </div>
                      <p className="text-sm text-red-700 mt-1">
                        These motorcycles have multiple potential GoFleet device matches. Please link them manually.
                      </p>
                    </div>
                    <div className="border-2 border-red-200 rounded-lg overflow-hidden max-h-[300px] overflow-y-auto">
                      <Table>
                        <TableHeader>
                          <TableRow className="bg-red-50">
                            <TableHead>Motorcycle Plate</TableHead>
                            <TableHead>Last 5 Chars</TableHead>
                            <TableHead>Conflicting Devices</TableHead>
                            <TableHead>Issue</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {matchResults.duplicates.map((item, index) => (
                            <TableRow key={index} className="bg-red-50/30 hover:bg-red-50/50">
                              <TableCell className="font-medium">{item.motorcycle.plate_number}</TableCell>
                              <TableCell>
                                <Badge variant="outline" className="font-mono bg-red-100 text-red-700">
                                  {item.motorcycle.last5}
                                </Badge>
                              </TableCell>
                              <TableCell>
                                <div className="space-y-1">
                                  {item.conflicting_devices.map((device, dIdx) => (
                                    <div key={dIdx} className="text-sm">
                                      <span className="font-medium">{device.name || device.license_plate}</span>
                                      {device.already_linked_to && (
                                        <Badge variant="outline" className="ml-2 text-xs bg-amber-100">
                                          Already linked to {device.already_linked_to}
                                        </Badge>
                                      )}
                                    </div>
                                  ))}
                                </div>
                              </TableCell>
                              <TableCell>
                                <Badge className="bg-red-100 text-red-700">
                                  <AlertTriangle className="w-3 h-3 mr-1" />
                                  Duplicate
                                </Badge>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-10 text-slate-500">
                    <CheckCircle2 className="w-12 h-12 mx-auto mb-2 opacity-50 text-green-500" />
                    <p>No duplicates found</p>
                  </div>
                )}
              </TabsContent>
            </Tabs>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default GoFleetDeviceManager;
